import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash/isEqual';
import {
  MuiBox,
  Drawer,
  Typography,
  Button,
  SvgIconButton,
  DotProgress,
  Card,
  Backdrop,
  FlexBox,
  InputField,
  Grid,
  color,
  SvgIcon
} from '@kis-technologies/kis-ui';
import { useActions } from 'hooks/useActions';
import { Client, actions as customersActions, selectors as customersSelectors } from '../../../../store/clients';
import { useStyles } from '../../styled';

type CustomerEditDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  client?: Client;
};

export const CustomerEditDrawer = ({ isOpen, onClose, client }: CustomerEditDrawerProps) => {
  const [updateCustomer, resetCustomerError] = useActions([customersActions.updateClient, customersActions.resetError]);
  const customersPending = useSelector(customersSelectors.selectPending);
  const classes = useStyles({ showDrawer: isOpen })();

  const [name, setName] = useState(client?.name || '');
  const [salesReportSettings, setSalesReportSettings] = useState('');

  const isStateFair = name?.trim() === 'State Fair of Texas';

  useEffect(() => {
    if (client) {
      setName(client.name);
      if (client.configuration?.salesReportSettings) {
        setSalesReportSettings(JSON.stringify(client.configuration.salesReportSettings));
      } else {
        setSalesReportSettings('');
      }
    }
  }, [client]);

  const handleSave = () => {
    if (!name.trim()) return;

    let parsedSalesSettings = {};
    if (isStateFair && salesReportSettings.trim()) {
      parsedSalesSettings = JSON.parse(salesReportSettings);
    }

    const payload = {
      id: client?.id,
      name: name.trim(),
      configuration: {
        ...(isStateFair && { salesReportSettings: parsedSalesSettings })
      }
    };

    updateCustomer(payload);
  };

  const handleClose = () => {
    resetCustomerError();
    onClose();
  };

  const isButtonDisabled = useMemo(() => {
    if (!name.trim()) return true;
    const originalName = client?.name?.trim() ?? '';
    const currentName = name.trim();
    if (!isStateFair) {
      return currentName === originalName;
    }
    const originalSettings = client?.configuration?.salesReportSettings ?? {};
    if (!salesReportSettings.trim()) return true;

    let parsedCurrent = {};
    try {
      parsedCurrent = JSON.parse(salesReportSettings);
    } catch {
      return true;
    }

    const hasNameChanged = currentName !== originalName;
    const hasSettingsChanged = !isEqual(parsedCurrent, originalSettings);

    return !(hasNameChanged || hasSettingsChanged);
  }, [name, client, isStateFair, salesReportSettings]);

  return (
    <>
      {customersPending && (
        <Backdrop open data-testid="customer-edit-backdrop">
          <DotProgress color={color.LIGHT_GRAY} />
        </Backdrop>
      )}

      <Drawer open={isOpen} onClose={handleClose} hasBorderRadius>
        <Grid container className={classes.customerEditOverlay}>
          <Card fullHeight>
            {/* Header */}
            <Grid className={classes.customerOverlayHeaderBlock}>
              <Grid className={classes.customerOverlayHeading}>
                <FlexBox>
                  <Typography fontWeight={600} className={classes.customerEditHeading}>
                    Edit Customer
                  </Typography>
                </FlexBox>
              </Grid>
              <Grid className={classes.customerOverlayCloseIcon}>
                <SvgIconButton appearance="transparent" onClick={handleClose} data-testid="update-customer-x">
                  <SvgIcon name="exit_circle" size={40} />
                </SvgIconButton>
              </Grid>
            </Grid>

            {/* Content */}
            <MuiBox className={classes.customerContentBox}>
              {/* Customer Name */}
              <InputField
                label="Name"
                type="text"
                size="medium"
                fullWidth
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
              />

              {/* State Fair JSON Input */}
              {isStateFair && (
                <MuiBox marginTop={3}>
                  <InputField
                    label="Sales Report Settings"
                    size="medium"
                    fullWidth
                    multiline
                    required
                    rows={14}
                    value={salesReportSettings}
                    onChange={(e) => setSalesReportSettings(e.target.value)}
                    sx={{ height: '400px' }}
                  />
                </MuiBox>
              )}

              {/* Save Button */}
              <MuiBox className={classes.saveButtonBox}>
                <Button
                  customSize="small"
                  appearance="primary"
                  onClick={handleSave}
                  className={classes.saveButton}
                  disabled={isButtonDisabled}
                >
                  Save
                </Button>
              </MuiBox>
            </MuiBox>
          </Card>
        </Grid>
      </Drawer>
    </>
  );
};
